import React, { useState, useRef, useCallback } from "react";

const fmtAxis = (v) => {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `$${v}`;
};

const fmtAxisNum = (v) => {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `${v}`;
};

// ─── Line Chart (single line) ─────────────────────────
function LineChart({ data, valueKey, labelKey, color, height = 240, formatValue }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  const fmt = formatValue || ((v) => `$${v.toLocaleString()}`);

  const svgW = 600, svgH = height;
  const padT = 16, padB = 28, padL = 54, padR = 16;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const pts = data.map((d, i) => ({
    x: padL + (i / Math.max(data.length - 1, 1)) * chartW,
    y: padT + chartH - (d[valueKey] / max) * chartH,
    val: d[valueKey],
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1].x},${padT + chartH} L${pts[0].x},${padT + chartH} Z`;

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const svgX = relX * svgW;
    const chartRel = (svgX - padL) / chartW;
    const idx = Math.round(chartRel * (data.length - 1));
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  }, [data.length, chartW]);

  return (
    <div ref={containerRef} style={{ position: "relative" }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height="100%" style={{ display: "block" }}>
        {ticks.map((f) => {
          const y = padT + chartH - f * chartH;
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#0A0A0F" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fill="#4a6d8c" fontSize="10" fontFamily="JetBrains Mono, monospace">{fmtAxis(Math.round(max * f))}</text>
            </g>
          );
        })}
        <path d={areaPath} fill={`${color}18`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={hoverIdx === i ? 5 : 3} fill={hoverIdx === i ? "#fff" : color} stroke={color} strokeWidth="2" />
        ))}
        {hoverIdx !== null && <line x1={pts[hoverIdx].x} y1={padT} x2={pts[hoverIdx].x} y2={padT + chartH} stroke="#5a8ab5" strokeWidth="1" strokeDasharray="4 4" />}
        {data.map((d, i) => (
          <text key={i} x={pts[i].x} y={svgH - 6} textAnchor="middle" fill={hoverIdx === i ? "#e0eaf4" : "#4a6d8c"} fontSize="10" fontWeight="600" fontFamily="DM Sans, sans-serif">{d[labelKey]}</text>
        ))}
      </svg>
      {hoverIdx !== null && (
        <div style={{
          position: "absolute",
          left: `${(pts[hoverIdx].x / svgW) * 100}%`,
          top: `${(pts[hoverIdx].y / svgH) * 100 - 12}%`,
          transform: "translateX(-50%) translateY(-100%)",
          background: "#FFFFFF", border: `1px solid ${color}`, borderRadius: 0, padding: "4px 10px",
          fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color, fontWeight: 600,
          whiteSpace: "nowrap", pointerEvents: "none", zIndex: 10, boxShadow: "0 4px 12px rgba(0,0,0,.4)",
        }}>
          <div style={{ fontSize: 11, color: "#e0eaf4", marginBottom: 2, fontFamily: "'Archivo', sans-serif" }}>{data[hoverIdx][labelKey]}</div>
          {fmt(data[hoverIdx][valueKey])}
        </div>
      )}
    </div>
  );
}

// ─── Dual Line Chart (Revenue vs Profit) ──────────────
function DualLineChart({ data, height = 240 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);
  const max = Math.max(...data.map((d) => Math.max(d.revenue, d.profit)), 1);

  const svgW = 600, svgH = height;
  const padT = 16, padB = 28, padL = 54, padR = 16;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const getY = (val) => padT + chartH - (val / max) * chartH;
  const getX = (i) => padL + (i / Math.max(data.length - 1, 1)) * chartW;

  const makeLine = (key) => data.map((d, i) => `${i === 0 ? "M" : "L"}${getX(i)},${getY(d[key])}`).join(" ");
  const makeArea = (key) => makeLine(key) + ` L${getX(data.length - 1)},${padT + chartH} L${getX(0)},${padT + chartH} Z`;

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const svgX = relX * svgW;
    const chartRel = (svgX - padL) / chartW;
    const idx = Math.round(chartRel * (data.length - 1));
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  }, [data.length, chartW]);

  return (
    <div ref={containerRef} style={{ position: "relative" }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height="100%" style={{ display: "block" }}>
        {ticks.map((f) => {
          const y = padT + chartH - f * chartH;
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#0A0A0F" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fill="#4a6d8c" fontSize="10" fontFamily="JetBrains Mono, monospace">{fmtAxis(Math.round(max * f))}</text>
            </g>
          );
        })}
        <path d={makeArea("revenue")} fill="#0033FF18" />
        <path d={makeLine("revenue")} fill="none" stroke="#0033FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={makeArea("profit")} fill="#34d39918" />
        <path d={makeLine("profit")} fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => (
          <circle key={`r${i}`} cx={getX(i)} cy={getY(d.revenue)} r={hoverIdx === i ? 5 : 3} fill={hoverIdx === i ? "#fff" : "#0033FF"} stroke="#0033FF" strokeWidth="2" />
        ))}
        {data.map((d, i) => (
          <circle key={`p${i}`} cx={getX(i)} cy={getY(d.profit)} r={hoverIdx === i ? 5 : 3} fill={hoverIdx === i ? "#fff" : "#34d399"} stroke="#34d399" strokeWidth="2" />
        ))}
        {hoverIdx !== null && <line x1={getX(hoverIdx)} y1={padT} x2={getX(hoverIdx)} y2={padT + chartH} stroke="#5a8ab5" strokeWidth="1" strokeDasharray="4 4" />}
        {data.map((d, i) => (
          <text key={i} x={getX(i)} y={svgH - 6} textAnchor="middle" fill={hoverIdx === i ? "#e0eaf4" : "#4a6d8c"} fontSize="10" fontWeight="600" fontFamily="DM Sans, sans-serif">{d.label}</text>
        ))}
      </svg>
      {hoverIdx !== null && (
        <div style={{
          position: "absolute",
          left: `${(getX(hoverIdx) / svgW) * 100}%`,
          top: `${(Math.min(getY(data[hoverIdx].revenue), getY(data[hoverIdx].profit)) / svgH) * 100 - 4}%`,
          transform: "translateX(-50%) translateY(-100%)",
          background: "#FFFFFF", border: "1px solid #0A0A0F", borderRadius: 0, padding: "8px 12px",
          fontSize: 13, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap",
          pointerEvents: "none", zIndex: 10, boxShadow: "0 4px 16px rgba(0,0,0,.4)",
        }}>
          <div style={{ fontSize: 12, color: "#e0eaf4", fontWeight: 600, marginBottom: 4, fontFamily: "'Archivo', sans-serif" }}>{data[hoverIdx].label}</div>
          <div style={{ color: "#0033FF" }}>Revenue: ${data[hoverIdx].revenue.toLocaleString()}</div>
          <div style={{ color: "#34d399" }}>Profit: ${data[hoverIdx].profit.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

// ─── Stacked Bar Chart ────────────────────────────────
const STACK_COLORS = ["#0033FF", "#34d399", "#5a8ab5", "#f97316", "#ef4444", "#a78bfa", "#f472b6", "#22d3ee"];

function StackedBarChart({ data, categories, height = 240, colorMap, formatValue, formatAxis: fmtAx }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);
  const fmtVal = formatValue || ((v) => `$${v.toLocaleString()}`);
  const axFmt = fmtAx || fmtAxis;

  const colors = colorMap || {};
  categories.forEach((c, i) => { if (!colors[c]) colors[c] = STACK_COLORS[i % STACK_COLORS.length]; });

  const totals = data.map((d) => categories.reduce((s, c) => s + (d[c] || 0), 0));
  const max = Math.max(...totals, 1);

  const svgW = 600, svgH = height;
  const padT = 16, padB = 28, padL = 54, padR = 16;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const barW = Math.min(40, (chartW / data.length) * 0.7);
  const gap = chartW / data.length;

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current || data.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const svgX = relX * svgW;
    const idx = Math.floor((svgX - padL) / gap);
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  }, [data.length, gap]);

  return (
    <div ref={containerRef} style={{ position: "relative" }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height="100%" style={{ display: "block" }}>
        {ticks.map((f) => {
          const y = padT + chartH - f * chartH;
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#0A0A0F" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} textAnchor="end" fill="#4a6d8c" fontSize="10" fontFamily="JetBrains Mono, monospace">{axFmt(Math.round(max * f))}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const cx = padL + i * gap + gap / 2;
          let yBottom = padT + chartH;
          return (
            <g key={i}>
              {categories.map((c) => {
                const val = d[c] || 0;
                const h = (val / max) * chartH;
                const y = yBottom - h;
                yBottom = y;
                return <rect key={c} x={cx - barW / 2} y={y} width={barW} height={h} fill={colors[c]} rx={2} opacity={hoverIdx === i ? 1 : 0.85} />;
              })}
              <text x={cx} y={svgH - 6} textAnchor="middle" fill={hoverIdx === i ? "#e0eaf4" : "#4a6d8c"} fontSize="10" fontWeight="600" fontFamily="DM Sans, sans-serif">{d.label}</text>
            </g>
          );
        })}
      </svg>
      {hoverIdx !== null && (
        <div style={{
          position: "absolute",
          left: `${((padL + hoverIdx * gap + gap / 2) / svgW) * 100}%`,
          top: 0, transform: "translateX(-50%)",
          background: "#FFFFFF", border: "1px solid #0A0A0F", borderRadius: 0, padding: "8px 12px",
          fontSize: 13, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap",
          pointerEvents: "none", zIndex: 10, boxShadow: "0 4px 16px rgba(0,0,0,.4)",
        }}>
          <div style={{ fontSize: 12, color: "#e0eaf4", fontWeight: 600, marginBottom: 4, fontFamily: "'Archivo', sans-serif" }}>{data[hoverIdx].label}</div>
          {categories.filter((c) => (data[hoverIdx][c] || 0) > 0).map((c) => (
            <div key={c} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
              <span style={{ width: 8, height: 8, borderRadius: 0, background: colors[c], flexShrink: 0 }} />
              <span style={{ color: "#b0c4de", fontFamily: "'Archivo', sans-serif", fontSize: 12 }}>{c}:</span>
              <span style={{ color: colors[c] }}>{fmtVal(data[hoverIdx][c] || 0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Horizontal Bar Chart ─────────────────────────────
function HorizontalBarChart({ data, height, formatValue }) {
  const fmt = formatValue || ((v) => v.toLocaleString());
  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d, i) => (
        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#b0c4de", minWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</span>
          <div style={{ flex: 1, height: 20, background: "#FFFFFF", borderRadius: 0, overflow: "hidden", position: "relative" }}>
            <div style={{
              height: "100%", width: `${(d.value / max) * 100}%`,
              background: d.color || STACK_COLORS[i % STACK_COLORS.length],
              borderRadius: 0, transition: "width .4s",
              minWidth: d.value > 0 ? 2 : 0,
            }} />
          </div>
          <span style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: d.color || "#0033FF", minWidth: 60, textAlign: "right" }}>{fmt(d.value)}</span>
        </div>
      ))}
    </div>
  );
}

export { LineChart, DualLineChart, StackedBarChart, HorizontalBarChart, fmtAxis, fmtAxisNum };
